import { LeaderboardTrialInvitesPopupComponent } from './../leaderboard-trial-invites-popup/leaderboard-trial-invites-popup.component';
import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Input,
    createEnvironmentInjector,
    ApplicationRef,
    inject,
} from '@angular/core';
import { Subscription, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { LeaderboardPreviewGoalService } from '../../service/leaderboard-preview-goal/leaderboard-preview-goal.service';
import { UserSimpleGoal } from '@app/leaderboard/model/user.model';
import { TrialBannerService } from '@app/shell/trial-banner/service/trial-banner.service';
import { PopupService } from '@app/shared/popup';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-leaderboard-preview-goal',
    templateUrl: './leaderboard-preview-goal.component.html',
    styleUrls: ['./leaderboard-preview-goal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LeaderboardPreviewGoalComponent implements OnInit, OnDestroy {
    /**
     * Count of users in leadearboard preview
     */
    @Input() count!: number;

    @Input() name!: string;
    @Input() widgetMode = false;

    private readonly appRef = inject(ApplicationRef);
    private readonly leaderboardPreviewGoalService = inject(LeaderboardPreviewGoalService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly trialBannerService = inject(TrialBannerService);
    private readonly popupService = inject(PopupService);

    readonly form = this.leaderboardPreviewGoalService.form;

    invitesTrial$ = this.trialBannerService.invitesTrial$;
    invitesLoading$ = this.invitesTrial$.pipe(map((data) => data.loading));

    private readonly subscriptions$ = new Subscription();

    get leaderboard() {
        return this.leaderboardPreviewGoalService.leaderboard(this.count);
    }

    get loading() {
        return this.leaderboardPreviewGoalService.loading;
    }

    get dropdownItems() {
        return this.leaderboardPreviewGoalService.ranges.map((name) => {
            return {
                id: name,
                name: this.rangeLabel(name),
            };
        });
    }

    get hasTrial() {
        return this.trialBannerService.hasTrial;
    }

    get sendedAllInvites() {
        const invites = this.trialBannerService.invitesTrial;
        return invites.max === invites.sent;
    }

    get showTrialInvite() {
        return this.hasTrial && !this.sendedAllInvites;
    }

    get showTrialInvitesInfo() {
        return this.hasTrial && this.leaderboard && this.leaderboard.length === 1;
    }

    ngOnInit() {
        this.subscriptions$.add(this.leaderboardPreviewGoalService.updates.pipe(tap(() => this.changeDetectorRef.detectChanges())).subscribe());

        if (this.hasTrial) {
            this.subscriptions$.add(this.invitesTrial$.pipe(tap(() => this.changeDetectorRef.detectChanges())).subscribe());
            this.subscriptions$.add(
                this.trialBannerService.updateLeaderboard$.pipe(tap(() => this.leaderboardPreviewGoalService.trialResetLeaderboard())).subscribe(),
            );
            if (this.trialBannerService.invitesTrial.loaded) {
                this.trialBannerService.getTrial(true);
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions$.unsubscribe();
    }

    itemTrendColor(item: UserSimpleGoal): string {
        switch (item.trend) {
            case 'success':
                return 'primary';
            case 'muted':
                return 'black';
            case 'danger':
                return 'danger';
        }
    }

    inviteTrialUsers() {
        if (!this.trialBannerService.invitesTrial.loading) {
            const environmentInjector = createEnvironmentInjector([{ provide: TrialBannerService, useValue: this.trialBannerService }], this.appRef.injector);

            this.popupService.open(LeaderboardTrialInvitesPopupComponent, null, null, { injector: environmentInjector });
        }
    }

    getUserFullName(user: UserSimpleGoal): string {
        return this.leaderboardPreviewGoalService.getUserFullName(user);
    }

    private rangeLabel(key: string) {
        switch (key) {
            case 'week':
                return this.translateService.instant('v3-preview-leaderboard-range-week');
            case 'month':
                return this.translateService.instant('v3-preview-leaderboard-range-month');
            case 'all-time':
                return this.translateService.instant('v3-preview-leaderboard-range-all-time');
            case 'alltime':
                return this.translateService.instant('v3-preview-leaderboard-range-alltime');
            case 'yeartodate':
                return this.translateService.instant('v3-preview-leaderboard-range-year-to-date');
        }
    }
}
